import React, { useMemo } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import {
  AudioProcessingReportType,
  AudioProcessingReportResponseType,
} from '@account_module/types/AudioProcessing';
import { useAudioProcessingTracking } from '@account_module/hooks/useAudioProcessingTracking';
import { PROCESSING_INTERVAL } from '@account_module/constants/audioProcessing';

import {
  extractDateFromISOString,
  dateWithoutTimezone,
  convertSecondsToHMS,
} from '@utils/dateUtils';
import { Loader } from '@components/Loader';

const Container = tw.div`flex flex-row gap-2 text-text-and-icon text-xs`;
const StyledSpan = tw.span`text-text-and-icon text-xs`;

const LoadingState = ({ month }: { month: string }) => {
  return (
    <Container>
      <StyledSpan>{month}</StyledSpan>
      <Loader size='xs' theme='dark' tw='inline-block mx-2' />
      <StyledSpan>|</StyledSpan>
      <Loader size='xs' theme='dark' tw='inline-block mx-2' />
    </Container>
  );
};

const Fallback = ({ month }: { month: string }) => {
  const { t } = useTranslation();
  return (
    <StyledSpan>
      {month} - {t('account_module.settings_page.audio_processing_report.no_data')}
    </StyledSpan>
  );
};

const currentDate = new Date();
const defaultValue = {
  start_date: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
  end_date: new Date(),
  interval: PROCESSING_INTERVAL.DAY,
};

export const _CurrentMonthAudioProcessingReport = () => {
  const { t } = useTranslation();

  const urlParams = new URLSearchParams({
    start_date: extractDateFromISOString(dateWithoutTimezone(defaultValue.start_date)) as string,
    end_date: extractDateFromISOString(dateWithoutTimezone(defaultValue?.end_date)) as string,
    interval: PROCESSING_INTERVAL.MONTH,
  }).toString();

  const { data: processingUsageData, isLoading } = useAudioProcessingTracking(urlParams);

  const processingSum = useMemo<AudioProcessingReportType>(() => {
    return processingUsageData?.reduce(
      (acc: AudioProcessingReportType, item: AudioProcessingReportResponseType) => ({
        audio_duration_sec: (acc.audio_duration_sec += item.audio_duration_sec),
        audio_count: (acc.audio_count += item.audio_count),
      }),
      { audio_duration_sec: 0, audio_count: 0 }
    );
  }, [processingUsageData]);

  const currentMonthString = useMemo(() => {
    const _date = processingUsageData?.[0]?.interval_segment
      ? new Date(processingUsageData[0].interval_segment)
      : new Date();

    return _date?.toLocaleString('default', {
      month: 'long',
    });
  }, [processingUsageData]);

  if (isLoading) {
    return <LoadingState month={currentMonthString} />;
  }

  if (!isLoading && !processingSum?.audio_count && !processingSum?.audio_duration_sec) {
    return <Fallback month={currentMonthString} />;
  }

  return (
    <Container>
      <StyledSpan>{currentMonthString}</StyledSpan>
      <StyledSpan>
        {t('account_module.settings_page.audio_processing_report.files_count', {
          count: processingSum?.audio_count,
        })}
      </StyledSpan>
      <StyledSpan>|</StyledSpan>
      <StyledSpan>{convertSecondsToHMS(processingSum?.audio_duration_sec)}h</StyledSpan>
    </Container>
  );
};

export const CurrentMonthAudioProcessingReport = React.memo(_CurrentMonthAudioProcessingReport);
